import * as React from "react";

import Image, { type StaticImageData } from "next/image";

import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";

export interface JetSmartButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "sm" | "default" | "lg" | "icon";
  children?: React.ReactNode;
  icon?: React.ReactNode;
  iconSrc?: string | StaticImageData;
  iconAlt?: string;
  iconClassName?: string; // e.g., "w-5 h-5"
  /** When true and `iconSrc` is an SVG, render it colorized to match text via CSS mask and `currentColor`. */
  colorizeSvg?: boolean;
  bgColor?: string;
  borderColor?: string;
  textColor?: string;
  hoverColor?: string;
  iconPosition?: "left" | "right";
}

export const JetSmartButton = React.forwardRef<HTMLButtonElement, JetSmartButtonProps>(
  (
    {
      size = "default",
      className,
      children,
      icon,
      iconSrc,
      iconAlt,
      iconClassName,
      colorizeSvg = true,
      bgColor,
      borderColor,
      textColor,
      hoverColor,
      iconPosition = "right",
      ...props
    },
    ref,
  ) => {
    const renderIcon = () => {
      if (!iconSrc) return icon ?? null;

      const url = typeof iconSrc === "string" ? iconSrc : iconSrc.src;
      const isSvg = /\.svg($|\?)/i.test(url);
      if (isSvg && colorizeSvg) {
        return (
          <span
            aria-hidden
            className={cn("inline-block shrink-0 align-middle", iconClassName)}
            style={
              {
                backgroundColor: "currentColor",
                maskImage: `url(${url})`,
                WebkitMaskImage: `url(${url})`,
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskPosition: "center",
                maskSize: "contain",
                WebkitMaskSize: "contain",
              } as React.CSSProperties
            }
          />
        );
      }
      return (
        <span className={cn("relative inline-block shrink-0 align-middle", iconClassName)}>
          <Image src={iconSrc} alt={iconAlt ?? ""} fill className="object-contain" />
        </span>
      );
    };

    return (
      <Button
        ref={ref}
        size={size}
        className={cn(
          "flex items-center justify-center gap-3 rounded-full border font-semibold transition-all duration-300 cursor-pointer hover:opacity-90 hover:scale-105",
          // caller-provided style tokens (preserve existing API)
          bgColor,
          borderColor,
          textColor,
          hoverColor,
          className,
        )}
        data-testid="jetsmart-button"
        {...props}
      >
        {iconPosition === "left" && renderIcon()}
        <span>{children}</span>
        {iconPosition === "right" && renderIcon()}
      </Button>
    );
  },
);

JetSmartButton.displayName = "JetSmartButton";
